// Users List Page
import React, { useState, useEffect } from 'react';
import { useAdminTranslation } from '../utils/i18nContext';
import { adminApi } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';

const Users = () => {
  const { t } = useAdminTranslation();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    loadUsers();
  }, [page, search]);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const result = await adminApi.getUsers(page, 50, search);
      setUsers(result.users);
      setTotalPages(result.pages);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-3xl font-bold text-white">{t('user_list')}</h2>
      </div>

      {/* Search */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardContent className="pt-6">
          <form onSubmit={handleSearch} className="flex gap-2">
            <Input
              placeholder={t('search_users')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="bg-[#1A1A1A] border-white/10 text-white"
            />
            <Button type="submit" className="bg-cyan-500 hover:bg-cyan-600">
              <Search className="w-4 h-4" />
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardContent className="pt-6">
          {loading ? (
            <div className="text-center text-gray-400 py-8">{t('loading')}</div>
          ) : users.length === 0 ? (
            <div className="text-center text-gray-400 py-8">{t('no_data')}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('name')}</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('email')}</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('provider')}</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('sessions')}</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">{t('created_at')}</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.user_id} className="border-b border-white/5 hover:bg-white/5">
                      <td className="py-3 px-4 text-white">{user.name}</td>
                      <td className="py-3 px-4 text-gray-300">{user.email}</td>
                      <td className="py-3 px-4">
                        <span className="px-2 py-1 bg-cyan-500/10 text-cyan-400 rounded text-xs">
                          {user.auth_provider || 'email'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-gray-300">{user.session_count || 0}</td>
                      <td className="py-3 px-4 text-gray-400 text-sm">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-white/10">
              <div className="text-sm text-gray-400">
                {t('page')} {page} {t('of')} {totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  variant="outline"
                  size="sm"
                  className="border-white/10"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  variant="outline"
                  size="sm"
                  className="border-white/10"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Users;
